import { createContext, useState } from "react";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import {
  getPaymentTransactionsService,
  getPaymentTransactionService,
  editPaymentTransactionService,
  deletePaymentTransactionService,
} from "./../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";

export const paymentTransactionAdminContext = createContext({
  codes: [],
  setCodes: () => {},
  paymentTransaction: {},
  setPaymentTransaction: () => {},
  search: "",
  setSearch: () => {},
  amount: "",
  setAmount: () => {},
  ordering: "",
  setOrdering: () => {},
  status: "",
  setStatus: () => {},
  totalCount: null,
  setTotalCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  limit: null,
  setLimit: () => {},
  handleClosePaymentTransactionModal: () => {},
  handleGetPaymentTransactions: () => {},
  handleGetPaymentTransaction: () => {},
  handleEditPaymentTransaction: () => {},
  handleDeletePaymentTransaction: () => {},
});

const PaymentTransactionAdminContext = ({ children, isUser = false }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const [paymentTransactions, setPaymentTransactions] = useState([]);
  const [paymentTransaction, setPaymentTransaction] = useState({});
  const [search, setSearch] = useState("");
  const [amount, setAmount] = useState("");
  const [ordering, setOrdering] = useState("-created");
  const [status, setStatus] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [limit, setLimit] = useState(100);

  // Handle Close Payment Transaction Modal
  const handleClosePaymentTransactionModal = () => {
    setPaymentTransaction({});
    setAmount("");
  };

  // Handle Get PaymentTransactions Method
  const handleGetPaymentTransactions = async (event) => {
    event?.preventDefault();
    const isSearch = !isEmpty(event);

    const objData = {
      search,
      offset: ((isSearch ? 1 : pageNumber) - 1) * limit,
      limit,
      ordering,
      status,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getPaymentTransactionsService(objData);
      if (status === 200) {
        setPaymentTransactions(data.results);
        setTotalCount(data.count);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Payment Transaction Method
  const handleGetPaymentTransaction = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getPaymentTransactionService(id);
      if (status === 200) {
        setPaymentTransaction(data);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Payment Transaction Method
  const handleEditPaymentTransaction = async () => {
    const objData = {
      id: paymentTransaction?.id,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editPaymentTransactionService(objData);
      if (status === 200 || status === 201) {
        toastSuccessMessage(
          `سابقه پرداخت ${paymentTransaction?.client?.first_name} ${paymentTransaction?.client?.last_name} با موفقیت ویرایش شد`
        );
        handleClosePaymentTransactionModal();
        if (!isUser) handleGetPaymentTransactions();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      if (!isUser) dispatch(setLoadingFalseAction());
    } finally {
      if (isUser) dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Payment Transaction Method
  const handleDeletePaymentTransaction = async (item) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deletePaymentTransactionService(item?.id);
      if (status === 200) {
        toastSuccessMessage(
          `سابقه پرداخت ${item?.client?.first_name} ${item?.client?.last_name} با موفقیت حذف شد`
        );
        handleGetPaymentTransactions();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <paymentTransactionAdminContext.Provider
      value={{
        paymentTransactions,
        setPaymentTransactions,
        paymentTransaction,
        setPaymentTransaction,
        search,
        setSearch,
        amount,
        setAmount,
        ordering,
        setOrdering,
        status,
        setStatus,
        totalCount,
        setTotalCount,
        pageNumber,
        setPageNumber,
        limit,
        setLimit,
        handleClosePaymentTransactionModal,
        handleGetPaymentTransactions,
        handleGetPaymentTransaction,
        handleEditPaymentTransaction,
        handleDeletePaymentTransaction,
      }}
    >
      {children}
    </paymentTransactionAdminContext.Provider>
  );
};

export default PaymentTransactionAdminContext;
